<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { connectRefinementList } from 'instantsearch.js/es/connectors';
import {
  RefinementListConnectorParams,
  RefinementListItem,
  RefinementListWidgetDescription,
} from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import { computed, onMounted, PropType, ref, watch, watchEffect } from 'vue';
// @ts-expect-error (function lacks types; this should be converted in the near future)
import { createSuitMixin } from 'vue-instantsearch/vue3/es';
import { useRoute } from 'vue-router';

import PlusIcon from '@/components/base/assets/PlusIcon.vue';
import Checkbox from '@/components/base/form/Checkbox.vue';
import TextButton from '@/components/base/TextButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import CollapsibleWidget from '@/components/search/widgets/CollapsibleWidget.vue';
import { useWidget } from '@/composables/instant-search/useWidget';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

const MAX_OPTIONS_TO_DISPLAY = 4;

defineOptions({
  mixins: [createSuitMixin({ name: 'RefinementList' })],
});

const props = defineProps({
  attribute: { required: true, type: String },
  header: { required: true, type: String },
  limit: { required: false, type: Number, default: 10 },
  modelValue: { required: false, type: Boolean, default: true },
  operator: {
    required: false,
    type: String as PropType<RefinementListConnectorParams['operator']>,
    default: 'or',
    validator(value) {
      return value === 'and' || value === 'or';
    },
  },
  optionCount: { required: false, type: Number, default: 0 },
  sortBy: {
    required: false,
    type: [Array, Function] as PropType<RefinementListConnectorParams['sortBy']>,
    default: () => ['isRefined', 'count:desc', 'name:asc'],
  },
  transformItems: {
    required: false,
    type: Function as PropType<RefinementListConnectorParams['transformItems']>,
  },
});

const emit = defineEmits(['click', 'update:modelValue', 'update:optionCount']);

const open = useVModel(props, 'modelValue', emit);

const route = useRoute();
const { flags } = useFeatureFlags();

const widgetParams = computed<RefinementListConnectorParams>(() => ({
  attribute: props.attribute,
  limit: props.limit,
  operator: props.operator,
  sortBy: props.sortBy,
  transformItems: props.transformItems,
}));

const { state } = useWidget<RefinementListWidgetDescription, RefinementListConnectorParams>(
  { connector: connectRefinementList },
  widgetParams,
);
const items = computed<RefinementListItem[]>(() => {
  const matchingItems = state.value?.items ?? [];
  const facetsRefinements = state.value?.instantSearchInstance.helper?.state.facetsRefinements;
  if (!facetsRefinements) return matchingItems;
  const selectedRefinements = facetsRefinements[props.attribute] ?? [];
  // some toggled refinements are not in the matching items, so we need to recreate them at the beginning of the list
  const missingItems = selectedRefinements
    .filter((value) => !matchingItems.some((item) => item.value === value))
    .map((value) => ({
      count: 0,
      isRefined: true,
      label: value,
      value,
    }));
  return missingItems.concat(matchingItems);
});

const displayAllOptions = ref(false);

const isSearchPage = computed(() => route?.name === 'Search');

const displayShowMoreButton = computed<boolean>(
  () =>
    items.value &&
    items.value.length > MAX_OPTIONS_TO_DISPLAY &&
    !displayAllOptions.value &&
    ((flags.showMoreFiltersPLP && !isSearchPage.value) ||
      (flags.showMoreFiltersSearchPage && isSearchPage.value)),
);

const showMenuPanel = computed(() => items.value.length > 0);
const refine = (value: string) => {
  state.value?.refine(value);
  emit('click');
};

const optionCount = useVModel(props, 'optionCount', emit);
onMounted(() => {
  watchEffect(() => {
    optionCount.value = items.value.length ?? 0;
  });
});

watch(open, (isOpen: boolean) => {
  if (isOpen) displayAllOptions.value = false;
});
</script>

<template>
  <div v-if="state">
    <CollapsibleWidget
      v-model="open"
      :showPanel="showMenuPanel"
      :attribute="attribute"
      :header="header"
    >
      <div :class="suit()">
        <ul :class="suit('list')" data-test="menu-list">
          <li
            v-for="item in displayShowMoreButton ? items.slice(0, MAX_OPTIONS_TO_DISPLAY) : items"
            :key="item.value"
            :class="[suit('item'), item.isRefined && suit('item', 'selected')]"
          >
            <Checkbox
              :modelValue="item.isRefined"
              @update:modelValue="refine(item.value)"
              :class="suit('label')"
              data-test="refinement-list-checkbox"
              :value="item.value"
              @keydown.enter="refine(item.value)"
            >
              <span :class="suit('labelText')">{{ item.label }}</span>
              <span :class="suit('count')">{{ item.count }}</span>
            </Checkbox>
          </li>
          <TextButton
            v-if="displayShowMoreButton"
            class="flex items-center gap-1 mb-2"
            :underline="false"
            @click="displayAllOptions = true"
          >
            <PlusIcon :size="16" />
            <BaseBodyText> Show more ({{ items.length - MAX_OPTIONS_TO_DISPLAY }}) </BaseBodyText>
          </TextButton>
        </ul>
      </div>
    </CollapsibleWidget>
  </div>
</template>
